import { useState, useRef, useCallback } from 'react';

interface UseProgressBarReturn {
  progress: number;
  startProgress: () => void;
  completeProgress: () => Promise<void>;
  resetProgress: () => void;
}

export function useProgressBar(): UseProgressBarReturn {
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearProgressInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startProgress = useCallback(() => {
    clearProgressInterval();
    setProgress(0);

    let currentProgress = 0;

    intervalRef.current = setInterval(() => {
      if (currentProgress < 30) {
        currentProgress += 2;
      } else if (currentProgress < 60) {
        currentProgress += 0.8;
      } else if (currentProgress < 80) {
        currentProgress += 0.4;
      } else if (currentProgress < 90) {
        currentProgress += 0.2;
      } else {
        currentProgress += 0.05;
      }

      currentProgress = Math.min(currentProgress, 95);
      setProgress(currentProgress);
    }, 200);
  }, [clearProgressInterval]);

  const completeProgress = useCallback(async () => {
    clearProgressInterval();
    setProgress(100);
    await new Promise(resolve => setTimeout(resolve, 300));
  }, [clearProgressInterval]);

  const resetProgress = useCallback(() => {
    clearProgressInterval();
    setProgress(0);
  }, [clearProgressInterval]);

  return { progress, startProgress, completeProgress, resetProgress };
}
